<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcBillingListItemHeader',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'deleted']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    newItem: $fn.value.pgettext('BillingListItem', 'New item'),
    editItem: $fn.value.pgettext('BillingListItem', 'Edit item'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    return $fn.value.pgettext('BillingListItem', 'Billing list item');
  });
  const resourceKey = computed(() => {
    if (loaded.value) {
      return props.item.isNew
        ? messages.value.newItem
        : messages.value.editItem;
    }
    return null;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  const act_1040167445267876_emit_mbic9rb3i_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_3601807637515735_emit_5nkznrf4a_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_3601807637515735_reloadSlickgrid_dyo6z04e7_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_3601807637515735_request_6g8zhizqh_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_item',
      data: {
        id: props.item.id,
        etag: props.item.etag,
        locked: true,
      },
    };
  };
  const act_4139696446929656_emit_dqnjs9mib_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4139696446929656_reloadSlickgrid_8zeflmcb0_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_4139696446929656_request_9xztd2ufa_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_item',
      data: {
        id: props.item.id,
        etag: props.item.etag,
        locked: false,
      },
    };
  };
  const act_4700643919467772_emit_nyc5dzzan_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4700643919467772_reloadSlickgrid_z8cgdc2se_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_4700643919467772_request_0aa5gq8ps_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_item',
      data: {
        id: props.item.id,
        etag: props.item.etag,
        void: true,
      },
    };
  };
  const act_295664594794476_emit_3lug9txx0_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_295664594794476_reloadSlickgrid_fafq24iok_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_295664594794476_request_mwdv6yo5c_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_item',
      data: {
        id: props.item.id,
        etag: props.item.etag,
        void: false,
      },
    };
  };
  const act_8474446275270171_emit_eaz423bgu_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };

  const act_1040167445267876_emit_mbic9rb3i = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_mbic9rb3i_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_1040167445267876_clickPreventStop_fcxz67j0q =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_mbic9rb3i,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_1040167445267876_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_clickPreventStop_fcxz67j0q],
      event: 'click.prevent.stop',
    });
  const act_3601807637515735_emit_5nkznrf4a = new actionTypes.EmitAction({
    actionArgs: act_3601807637515735_emit_5nkznrf4a_getActionArgs,
  });
  const inst_evh_3601807637515735_success_el6bghjcf =
    new actionTypes.EventHandlerImpl({
      action: act_3601807637515735_emit_5nkznrf4a,
      event: 'success',
      displayName: 'emit',
    });
  const act_3601807637515735_reloadSlickgrid_dyo6z04e7 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_3601807637515735_reloadSlickgrid_dyo6z04e7_getActionArgs,
    });
  const inst_evh_3601807637515735_success_ac96xvfoq =
    new actionTypes.EventHandlerImpl({
      action: act_3601807637515735_reloadSlickgrid_dyo6z04e7,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_3601807637515735_request_6g8zhizqh = new actionTypes.RequestAction({
    actionArgs: act_3601807637515735_request_6g8zhizqh_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_3601807637515735_success_el6bghjcf,
      inst_evh_3601807637515735_success_ac96xvfoq,
    ],
  });
  const inst_evh_3601807637515735_clickPreventStop_p17vzlh25 =
    new actionTypes.EventHandlerImpl({
      action: act_3601807637515735_request_6g8zhizqh,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_3601807637515735_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3601807637515735_clickPreventStop_p17vzlh25],
      event: 'click.prevent.stop',
    });
  const act_4139696446929656_emit_dqnjs9mib = new actionTypes.EmitAction({
    actionArgs: act_4139696446929656_emit_dqnjs9mib_getActionArgs,
  });
  const inst_evh_4139696446929656_success_jeq6nzf78 =
    new actionTypes.EventHandlerImpl({
      action: act_4139696446929656_emit_dqnjs9mib,
      event: 'success',
      displayName: 'emit',
    });
  const act_4139696446929656_reloadSlickgrid_8zeflmcb0 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_4139696446929656_reloadSlickgrid_8zeflmcb0_getActionArgs,
    });
  const inst_evh_4139696446929656_success_8z1dk5zxo =
    new actionTypes.EventHandlerImpl({
      action: act_4139696446929656_reloadSlickgrid_8zeflmcb0,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_4139696446929656_request_9xztd2ufa = new actionTypes.RequestAction({
    actionArgs: act_4139696446929656_request_9xztd2ufa_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_4139696446929656_success_jeq6nzf78,
      inst_evh_4139696446929656_success_8z1dk5zxo,
    ],
  });
  const inst_evh_4139696446929656_clickPreventStop_cldhfox1z =
    new actionTypes.EventHandlerImpl({
      action: act_4139696446929656_request_9xztd2ufa,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_4139696446929656_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4139696446929656_clickPreventStop_cldhfox1z],
      event: 'click.prevent.stop',
    });
  const act_4700643919467772_emit_nyc5dzzan = new actionTypes.EmitAction({
    actionArgs: act_4700643919467772_emit_nyc5dzzan_getActionArgs,
  });
  const inst_evh_4700643919467772_success_ruaafr7lv =
    new actionTypes.EventHandlerImpl({
      action: act_4700643919467772_emit_nyc5dzzan,
      event: 'success',
      displayName: 'emit',
    });
  const act_4700643919467772_reloadSlickgrid_z8cgdc2se =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_4700643919467772_reloadSlickgrid_z8cgdc2se_getActionArgs,
    });
  const inst_evh_4700643919467772_success_nm58frta4 =
    new actionTypes.EventHandlerImpl({
      action: act_4700643919467772_reloadSlickgrid_z8cgdc2se,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_4700643919467772_request_0aa5gq8ps = new actionTypes.RequestAction({
    actionArgs: act_4700643919467772_request_0aa5gq8ps_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_4700643919467772_success_ruaafr7lv,
      inst_evh_4700643919467772_success_nm58frta4,
    ],
  });
  const inst_evh_4700643919467772_clickPreventStop_2dwdkot91 =
    new actionTypes.EventHandlerImpl({
      action: act_4700643919467772_request_0aa5gq8ps,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_4700643919467772_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4700643919467772_clickPreventStop_2dwdkot91],
      event: 'click.prevent.stop',
    });
  const act_295664594794476_emit_3lug9txx0 = new actionTypes.EmitAction({
    actionArgs: act_295664594794476_emit_3lug9txx0_getActionArgs,
  });
  const inst_evh_295664594794476_success_w8jfconnr =
    new actionTypes.EventHandlerImpl({
      action: act_295664594794476_emit_3lug9txx0,
      event: 'success',
      displayName: 'emit',
    });
  const act_295664594794476_reloadSlickgrid_fafq24iok =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_295664594794476_reloadSlickgrid_fafq24iok_getActionArgs,
    });
  const inst_evh_295664594794476_success_69l2wo0a6 =
    new actionTypes.EventHandlerImpl({
      action: act_295664594794476_reloadSlickgrid_fafq24iok,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_295664594794476_request_mwdv6yo5c = new actionTypes.RequestAction({
    actionArgs: act_295664594794476_request_mwdv6yo5c_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_295664594794476_success_w8jfconnr,
      inst_evh_295664594794476_success_69l2wo0a6,
    ],
  });
  const inst_evh_295664594794476_clickPreventStop_jtbmg4ktj =
    new actionTypes.EventHandlerImpl({
      action: act_295664594794476_request_mwdv6yo5c,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_295664594794476_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_295664594794476_clickPreventStop_jtbmg4ktj],
      event: 'click.prevent.stop',
    });
  const act_8474446275270171_emit_eaz423bgu = new actionTypes.EmitAction({
    actionArgs: act_8474446275270171_emit_eaz423bgu_getActionArgs,
  });
  const inst_evh_8474446275270171_success_jpi5gmlxh =
    new actionTypes.EventHandlerImpl({
      action: act_8474446275270171_emit_eaz423bgu,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_8474446275270171_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8474446275270171_success_jpi5gmlxh],
    event: 'success',
  });

  const actions = {
    inst_evh_1040167445267876_clickPreventStop_fcxz67j0q,
    reload: inst_evh_1040167445267876_clickPreventStop_fcxz67j0q,
    inst_evh_1040167445267876_clickPreventStop,
    inst_evh_3601807637515735_success_el6bghjcf,
    inst_evh_3601807637515735_success_ac96xvfoq,
    inst_evh_3601807637515735_clickPreventStop_p17vzlh25,
    lock: inst_evh_3601807637515735_clickPreventStop_p17vzlh25,
    inst_evh_3601807637515735_clickPreventStop,
    inst_evh_4139696446929656_success_jeq6nzf78,
    inst_evh_4139696446929656_success_8z1dk5zxo,
    inst_evh_4139696446929656_clickPreventStop_cldhfox1z,
    inst_evh_4139696446929656_clickPreventStop,
    inst_evh_4700643919467772_success_ruaafr7lv,
    inst_evh_4700643919467772_success_nm58frta4,
    inst_evh_4700643919467772_clickPreventStop_2dwdkot91,
    inst_evh_4700643919467772_clickPreventStop,
    inst_evh_295664594794476_success_w8jfconnr,
    inst_evh_295664594794476_success_69l2wo0a6,
    inst_evh_295664594794476_clickPreventStop_jtbmg4ktj,
    inst_evh_295664594794476_clickPreventStop,
    inst_evh_8474446275270171_success_jpi5gmlxh,
    inst_evh_8474446275270171_success,
  };

  const evh_1040167445267876_clickPreventStop_fcxz67j0q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop_fcxz67j0q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_success_el6bghjcf = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_success_el6bghjcf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_success_ac96xvfoq = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_success_ac96xvfoq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_clickPreventStop_p17vzlh25 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3601807637515735_clickPreventStop_p17vzlh25.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4139696446929656_success_jeq6nzf78 = async (event, scope) => {
    return await actions.inst_evh_4139696446929656_success_jeq6nzf78.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4139696446929656_success_8z1dk5zxo = async (event, scope) => {
    return await actions.inst_evh_4139696446929656_success_8z1dk5zxo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4139696446929656_clickPreventStop_cldhfox1z = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4139696446929656_clickPreventStop_cldhfox1z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4139696446929656_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4139696446929656_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4700643919467772_success_ruaafr7lv = async (event, scope) => {
    return await actions.inst_evh_4700643919467772_success_ruaafr7lv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4700643919467772_success_nm58frta4 = async (event, scope) => {
    return await actions.inst_evh_4700643919467772_success_nm58frta4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4700643919467772_clickPreventStop_2dwdkot91 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4700643919467772_clickPreventStop_2dwdkot91.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4700643919467772_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4700643919467772_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_295664594794476_success_w8jfconnr = async (event, scope) => {
    return await actions.inst_evh_295664594794476_success_w8jfconnr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_295664594794476_success_69l2wo0a6 = async (event, scope) => {
    return await actions.inst_evh_295664594794476_success_69l2wo0a6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_295664594794476_clickPreventStop_jtbmg4ktj = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_295664594794476_clickPreventStop_jtbmg4ktj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_295664594794476_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_295664594794476_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8474446275270171_success_jpi5gmlxh = async (event, scope) => {
    return await actions.inst_evh_8474446275270171_success_jpi5gmlxh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8474446275270171_success = async (event, scope) => {
    return await actions.inst_evh_8474446275270171_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpBox direction="column">
      <ItpText muted :text="resourceTitle" class="mb-0" v-if="resourceTitle">
      </ItpText>
      <ItpText tag="h5" :text="resourceKey" class="mb-0"> </ItpText>
    </ItpBox>
    <BButtonGroup class="ms-auto">
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        class="ms-auto"
        v-if="!item.isNew"
        @click.prevent.stop="evh_1040167445267876_clickPreventStop($event, {})"
      >
      </ItpButton>
      <BDropdown right variant="light" class="border" v-if="!item.isNew">
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Lock')"
          :tooltip="pgettext('BtnLockTooltip', 'Lock')"
          variant="default"
          v-if="!item.isNew && item.meta && item.meta.can_lock"
          @click.prevent.stop="
            evh_3601807637515735_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Unlock')"
          :tooltip="pgettext('BtnLockTooltip', 'Unlock')"
          variant="default"
          v-if="!item.isNew && item.meta && item.meta.can_unlock"
          @click.prevent.stop="
            evh_4139696446929656_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Invalidate')"
          :tooltip="pgettext('BtnLockTooltip', 'Invalidate')"
          variant="default"
          v-if="!item.isNew && item.meta && item.meta.can_invalidate"
          @click.prevent.stop="
            evh_4700643919467772_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Revoke invalidated')"
          :tooltip="pgettext('BtnLockTooltip', 'Revoke invalidated')"
          variant="default"
          v-if="!item.isNew && item.meta && item.meta.can_revoke_invalidation"
          @click.prevent.stop="evh_295664594794476_clickPreventStop($event, {})"
        >
        </ItpButton>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="item"
          :tooltip="pgettext('Resource|Command', 'Delete')"
          v-if="!item.isNew && !item.isReadOnly"
          @success="evh_8474446275270171_success($event, {})"
        >
        </BtnDeleteResource>
      </BDropdown>
    </BButtonGroup>
  </ItpBox>
</template>
